﻿import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BackButton from "../Common/BackButton";
import CreateWorkshopPanel from "./CreateWorkshopPanel";
import { useSearchParams } from "react-router-dom";
import { useStudio } from "../../contexts/StudioContext";
import { usePublicStudio } from "../../contexts/PublicStudioContext";
import { workshopsApi } from "../../lib/mudoNetwork";
import { useNotifications } from "../../contexts/NotificationsContext";
import { useWorkshops } from "../../contexts/WorkshopContext";
import { usePlanner } from "../../contexts/PlannerContext";
import { publishWorkshop, unpublishWorkshop } from "@/lib/workshops/publicSync";
import { supabase } from "../../supabaseClient";
import { studiosApi } from "../../lib/mudoNetwork";
import useStripePayoutsGuard from "../../hooks/useStripePayoutsGuard";
import { useMaterials } from "../../contexts/MaterialContext";

function glassStyle(isDark) {
  return {
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.60)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.08)",
    borderRadius: "24px",
    boxShadow:
      "0 18px 45px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.18)",
    transition: "background-color 200ms, border-color 200ms, box-shadow 200ms",
  };
}

const innerMotion = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

const modalMotion = {
  initial: { opacity: 0, scale: 0.96, y: 10 },
  animate: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
  exit: { opacity: 0, scale: 0.96, y: 10, transition: { duration: 0.2, ease: "easeIn" } },
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "AUD" });
const fmtWhen = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function WorkshopPage({ isDark = false, onBack }) {
  const [searchParams] = useSearchParams();
  const deepLinkBookingId = searchParams.get("bookingId");
  const deepLinkHandledRef = useRef(false);
  const [highlightWorkshopId, setHighlightWorkshopId] = useState(null);
  const { studio } = useStudio() || {};
  const studioId = studio?.studioId; // Use actual studio ID from public.studios, not localStorage
  const { publicProfile } = usePublicStudio() || {};
  const { notifyAndInbox } = useNotifications() || {};
  const { isStripeConnected, ensureStripeConnected } = useStripePayoutsGuard();
  const { upsertEvents } = usePlanner();
  const { materials: inventoryMaterials = [] } = useMaterials();
  const {
    workshops: localWorkshops,
    bookings,
    upsertWorkshop,
    deleteWorkshop: deleteLocalWorkshop,
  } = useWorkshops();

  const materialsDatalistId = "workshop-edit-materials-datalist";
  const inventoryMaterialNames = useMemo(() => {
    const names = (inventoryMaterials || [])
      .map((m) => String(m?.name || "").trim())
      .filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [inventoryMaterials]);
  const [loading, setLoading] = useState(true);
  const [workshops, setWorkshops] = useState([]);
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [duplicateWorkshop, setDuplicateWorkshop] = useState(null);
  
  // Workshop lifecycle management (delete vs cancel)
  const [workshopBookings, setWorkshopBookings] = useState({}); // {workshopId: {bookingsCount, hasPaidBookings}}
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingWorkshop, setCancellingWorkshop] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // ✅ CRITICAL: Persist visibility toggle state to both Supabase and local storage
  // This prevents the toggle from snapping back after publish/unpublish
  const updateLocalWorkshopAndPersist = async (updatedWorkshop) => {
    try {
      const workshopId = updatedWorkshop.id;
      const visibilityState = updatedWorkshop.is_published === true || updatedWorkshop.isPublic === true;
      
      // Build the complete workshop record with EXPLICIT field mapping
      // This ensures both is_published and isPublic are ALWAYS synchronized
      const localFormat = {
        id: workshopId,
        // Copy ALL fields from updatedWorkshop
        ...updatedWorkshop,
        // ENFORCE both visibility fields are synchronized
        isPublic: visibilityState,
        is_published: visibilityState,
      };

      console.log("[WorkshopPage] PERSIST: Saving to WorkshopContext with state:", { 
        id: localFormat.id, 
        title: localFormat.title,
        isPublic: localFormat.isPublic,
        is_published: localFormat.is_published
      });

      // 1) Persist DIRECTLY to WorkshopContext - this is the source of truth
      await upsertWorkshop(localFormat);
      
      console.log("[WorkshopPage] PERSIST: Saved to WorkshopContext, updating memory state");
      
      // 2) Update in-memory workshops state SYNCHRONOUSLY to stay in sync
      setWorkshops((prev) => {
        const updated = prev.map((w) => {
          if (w.id === workshopId) {
            const mergedWorkshop = { 
              ...w, 
              ...localFormat,
              isPublic: visibilityState, 
              is_published: visibilityState 
            };
            console.log("[WorkshopPage] PERSIST: Updated workshop in memory:", {
              id: mergedWorkshop.id,
              isPublic: mergedWorkshop.isPublic,
              is_published: mergedWorkshop.is_published
            });
            return mergedWorkshop;
          }
          return w;
        });
        return updated;
      });
    } catch (err) {
      console.error("[WorkshopPage] PERSIST: Failed to persist workshop locally", err);
      throw err;
    }
  };

  // Single source of truth: Supabase workshops.is_published (by workshop_key)
  // CRITICAL: This now persists locally to prevent UI snapping back
  const handleVisibilityToggle = async (checked) => {
    console.log("[WorkshopPage] Toggle visibility:", { checked, workshopId: editForm?.id });
    
    try {
      const localWorkshop = editForm;
      if (!localWorkshop?.id) {
        throw new Error("Workshop has no ID – cannot sync visibility");
      }

      const workshopId = localWorkshop.id;
      const workshopKey = localWorkshop.workshop_key || localWorkshop.id;

      // Optimistic UI update
      setEditForm((prev) => (prev ? { ...prev, is_published: checked, isPublic: checked } : prev));
      setWorkshops((prev) => prev.map((w) => (w.id === workshopId ? { ...w, is_published: checked, isPublic: checked } : w)));

      if (checked) {
        // PUBLISH — upsert to Supabase
        const durationMinutes = Number(localWorkshop.duration_minutes) || 120;
        const startAt = localWorkshop.start_at || new Date().toISOString();
        const endAt = localWorkshop.end_at || new Date(new Date(startAt).getTime() + durationMinutes * 60000).toISOString();

        console.log("[WorkshopPage] Publishing to Supabase...");
        await publishWorkshop({
          supabase,
          workshop: {
            id: workshopId,
            workshop_key: workshopKey,
            title: localWorkshop.title,
            description: localWorkshop.description ?? null,
            skill_level: localWorkshop.skill_level ?? null,
            start_at: startAt,
            end_at: endAt,
            duration_minutes: durationMinutes,
            capacity: localWorkshop.capacity ?? 0,
            seats_taken: localWorkshop.seats_taken ?? 0,
            price_cents: localWorkshop.price_cents ?? 0,
            currency: localWorkshop.currency ?? "AUD",
            hero_image_url: localWorkshop.hero_image_url || localWorkshop.teaserDataUrl || null,
          },
          studioId,
        });

        console.log("[WorkshopPage] Published to Supabase, now persisting locally...");
        
        // Persist published state locally
        await updateLocalWorkshopAndPersist({
          ...localWorkshop,
          id: workshopId,
          workshop_key: workshopKey,
          is_published: true,
          isPublic: true,
        });
        
        console.log("[WorkshopPage] Published state persisted, LOCKING editForm to true");
        
        // LOCK the editForm to ensure it stays true
        setEditForm((prev) => {
          if (!prev) return prev;
          return { ...prev, is_published: true, isPublic: true };
        });
      } else {
        // UNPUBLISH — update Supabase (by workshop_key)
        console.log("[WorkshopPage] Unpublishing from Supabase...");
        await unpublishWorkshop({ supabase, workshopIdOrKey: workshopKey });

        console.log("[WorkshopPage] Unpublished from Supabase, now persisting locally...");
        
        // Persist unpublished state locally
        await updateLocalWorkshopAndPersist({
          ...localWorkshop,
          id: workshopId,
          workshop_key: workshopKey,
          is_published: false,
          isPublic: false,
        });
        
        console.log("[WorkshopPage] Unpublished state persisted, LOCKING editForm to false");
        
        // LOCK the editForm to ensure it stays false
        setEditForm((prev) => {
          if (!prev) return prev;
          return { ...prev, is_published: false, isPublic: false };
        });
      }

      // Notify other tabs/pages
      try {
        window.dispatchEvent(
          new CustomEvent("workshops:changed", {
            detail: {
              action: checked ? "published" : "archived",
              workshopId: workshopId,
              workshopKey: workshopKey,
            },
          })
        );
      } catch {}
    } catch (err) {
      console.error("[Studio] Visibility toggle failed", err);

      // Revert optimistic UI on error
      const workshop = editForm;
      if (workshop?.id) {
        setEditForm((prev) => (prev ? { ...prev, is_published: !checked, isPublic: !checked } : prev));
        setWorkshops((prev) => prev.map((w) => (w.id === workshop.id ? { ...w, is_published: !checked, isPublic: !checked } : w)));
      }

      alert(err?.message || "Failed to update workshop visibility");
    }
  };

  useEffect(() => {
    if (!deepLinkBookingId || deepLinkHandledRef.current) return;
    if (!supabase) return;

    (async () => {
      try {
        const { data: booking, error } = await supabase
          .from("bookings")
          .select("id, workshop_id")
          .eq("id", deepLinkBookingId)
          .maybeSingle();
        if (error || !booking?.workshop_id) {
          notifyAndInbox?.({
            notification: {
              id: `workshop-booking-missing-${deepLinkBookingId}`,
              title: "Booking not found",
              message: "We couldn't find that workshop booking.",
              level: "warning",
              source: "bookings",
              world: "studio",
              entity: { type: "booking", id: deepLinkBookingId },
              action: {
                kind: "NAVIGATE",
                to: "/dashboard/workshops",
                params: { bookingId: deepLinkBookingId },
              },
            },
          });
          deepLinkHandledRef.current = true;
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete("bookingId");
            window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
          } catch {}
          return;
        }
        setHighlightWorkshopId(booking.workshop_id);
        deepLinkHandledRef.current = true;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("bookingId");
          window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
        } catch {}
      } catch (err) {
        console.warn("[WorkshopPage] booking deep-link error", err);
      }
    })();
  }, [deepLinkBookingId, notifyAndInbox]);

  // Dev-only utility: Remove remote workshops that don't exist in local WorkshopContext
  async function handleClearGhostRemoteWorkshops() {
    try {
      console.log("[WorkshopPage] Clearing ghost remote workshops...");

      // 1) Build a set of local IDs and titles
      const local = localWorkshops || [];
      const localIds = new Set(local.map(w => String(w.id || "")));
      const localTitles = new Set(
        local
          .map(w => (w.title || "").trim().toLowerCase())
          .filter(Boolean)
      );

      // 2) Fetch all remote workshops for current studio
      const { data: remote, error } = await workshopsApi.listWorkshopsForCurrentStudio();
      if (error) {
        console.error("[WorkshopPage] Failed to fetch remote workshops", error);
        alert("Could not load remote workshops. Check console for details.");
        return;
      }

      // 3) Find ghosts: remote items not in local by ID or title
      const ghosts = (remote || []).filter(r => {
        const id = String(r.id || "");
        const title = (r.title || "").trim().toLowerCase();
        const isLocalId = id && localIds.has(id);
        const isLocalTitle = title && localTitles.has(title);
        return !isLocalId && !isLocalTitle;
      });

      if (!ghosts.length) {
        console.log("[WorkshopPage] No ghost workshops found.");
        alert("No ghost workshops found in Supabase.");
        return;
      }

      console.log("[WorkshopPage] Found ghost workshops:", ghosts);

      // 4) Delete ghosts one by one
      for (const g of ghosts) {
        try {
          console.log("[WorkshopPage] Deleting ghost workshop", g.id, g.title);
          await workshopsApi.deleteWorkshop(g.id);
        } catch (err) {
          console.error("[WorkshopPage] Failed to delete ghost workshop", g.id, err);
          if (err?.code === '401' || err?.code === '403') {
            alert("Not authorized to delete ghost workshops. Sign in and ensure you own this studio.");
            return;
          }
        }
      }

      alert(`Deleted ${ghosts.length} ghost workshop(s) from Supabase.`);
    } catch (err) {
      console.error("[WorkshopPage] Error while clearing ghost workshops", err);
      alert("Something went wrong while clearing ghost workshops. See console.");
    }
  }

  // Use local workshops from context - vault is source of truth for Studio world
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);

      // Vault/local workshops from context are the source of truth
      // We sync TO Supabase when creating/editing/deleting, but don't load FROM Supabase here
      // This prevents deleted workshops from resurrecting on page refresh
      const local = localWorkshops || [];
      const localFormatted = local.map((w) => {
        const visibility = w.is_published === true || w.isPublic === true;
        return {
          ...w,
          start_at: w.startISO,
          end_at: w.startISO,
          type: w.type,
          skill_level: w.level,
          price_cents: (w.price || 0) * 100,
          discountPercent: w.discountPercent || 0,
          currency: "AUD",
          is_published: visibility,
          isPublic: visibility,
          seats_taken:
            bookings?.filter((b) => b.workshopId === w.id)
              .reduce((sum, b) => sum + (Number(b.seats) || 0), 0) || 0,
          archivedAt: w.archivedAt || w.archived_at || null,
        };
      });

      console.log(
        `[WorkshopPage] Loaded from WorkshopContext - Local:${localFormatted.length} Public:${
          localFormatted.filter((w) => w.isPublic).length
        }`
      );
      
      // Log first workshop's visibility state for debugging
      if (localFormatted.length > 0) {
        const first = localFormatted[0];
        console.log("[WorkshopPage] First workshop visibility:", {
          id: first.id,
          title: first.title,
          isPublic: first.isPublic,
          is_published: first.is_published,
          rawFromContext: {
            isPublic: local[0].isPublic,
            is_published: local[0].is_published
          }
        });
      }

      if (alive) {
        setWorkshops(localFormatted);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [localWorkshops, bookings]);

  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [level, setLevel] = useState("all");
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const glassInputClass =
    "w-full rounded-2xl border border-white/20 bg-white/10 text-white placeholder-white/45 px-3 py-2.5 focus:ring-2 focus:ring-amber-300 focus:border-transparent outline-none transition-all shadow-[0_0_0_1px_rgba(255,255,255,0.05)]";

  const now = Date.now();

  const typeOptions = useMemo(() => {
    const s = new Set();
    (workshops || []).forEach((w) => w?.type && s.add(String(w.type).toLowerCase()));
    return ["all", ...Array.from(s)];
  }, [workshops]);

  const levelOptions = useMemo(() => {
    const s = new Set();
    (workshops || []).forEach((w) => w?.skill_level && s.add(String(w.skill_level).toLowerCase()));
    return ["all", ...Array.from(s)];
  }, [workshops]);

  const getStartMs = (w) => {
    try {
      const iso = w?.start_at || w?.startISO || null;
      if (!iso) return 0;
      const ms = new Date(iso).getTime();
      return Number.isFinite(ms) ? ms : 0;
    } catch { return 0; }
  };

  const filtered = useMemo(() => {
    return [...(workshops || [])]
      .filter((w) => {
        if (showPast) return true; // include all
        const ms = getStartMs(w);
        return ms >= now;
      })
      .filter((w) =>
        type === "all" ? true : String(w.type || "").toLowerCase() === type
      )
      .filter((w) =>
        level === "all" ? true : String(w.skill_level || "").toLowerCase() === level
      )
      .filter((w) => {
        if (!q.trim()) return true;
        const hay = `${w.title || ""} ${w.description || ""} ${
          w.type || ""
        } ${w.skill_level || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
      .filter((w) => {
        if (!onlyAvailable) return true;
        const taken = Number(w.seats_taken || 0);
        return taken < (Number(w.capacity) || 0);
      })
      .filter((w) => {
        if (showArchived) return true;
        const isArchived = Boolean(w.archivedAt || w.archived_at);
        return !isArchived;
      })
        .sort((a, b) => getStartMs(a) - getStartMs(b));
      }, [workshops, q, type, level, onlyAvailable, now, showPast, showArchived]);

  useEffect(() => {
    if (!highlightWorkshopId) return;
    const el = document.getElementById(`workshop-${highlightWorkshopId}`);
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [highlightWorkshopId, filtered]);

  const chip = (label) => (
    <span
      className={`px-2 py-1 text-[11px] rounded-full ${
        isDark ? "bg-white/10" : "bg-black/10"
      }`}
    >
      {label}
    </span>
  );

  return (
    <div
      className={`min-h-screen ${isDark ? "text-white" : "text-neutral-900"}`}
      style={{
        backgroundColor: "var(--color-app-bg)",
        transition: "background-color 200ms, color 200ms",
      }}
    >
      <div className="sticky top-0 z-20 p-4 border-b" style={glassStyle(isDark)}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src="/src/assets/mudo-logo-bg.png"
              alt="Mudo"
              className="w-5 h-5 opacity-90"
            />
            <h1 className="text-2xl font-bold">Workshops</h1>
            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={handleClearGhostRemoteWorkshops}
                className="text-xs px-3 py-1 rounded-full border border-orange-400/60 bg-black/40 hover:bg-black/60 transition-colors"
                title="Remove remote workshops that don't exist locally"
              >
                Clear ghost remote workshops
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreatePanel(!showCreatePanel)}
              className="px-4 py-2 rounded-full text-white transition-colors shadow-md"
              style={{ backgroundColor: "var(--color-primary)" }}
              title={showCreatePanel ? "Hide creation panel" : "Create new workshop"}
            >
              {showCreatePanel ? "Hide Create" : "+ Create Workshop"}
            </button>
            {onBack ? (
              <button
                onClick={onBack}
                className="px-4 py-2 rounded-full text-white transition-colors shadow-md"
                style={{ backgroundColor: "var(--color-primary)" }}
                title="Back to Dashboard"
              >
                &larr; Back to Dashboard
              </button>
            ) : (
              <BackButton to="/dashboard" label="Back to Dashboard" />
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Filters */}
        <div
          className="p-4 rounded-3xl grid grid-cols-1 md:grid-cols-4 gap-3"
          style={glassStyle(isDark)}
          role="region"
          aria-label="Workshop filters"
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, description, type, or level"
            className={`px-3 py-2 rounded-2xl outline-none border transition ${
              isDark
                ? "bg-white/10 border-white/20 text-white placeholder-white/70"
                : "bg-white border-black/10 text-neutral-900 placeholder-neutral-500"
            }`}
            aria-label="Search"
            title="Search title, description, type, or level"
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className={`px-3 py-2 rounded-2xl border transition ${
              isDark
                ? "bg-white/10 border-white/20 text-white [&>option]:text-black [&>option]:bg-white"
                : "bg-white border-black/10 text-neutral-900"
            }`}
            aria-label="Filter by type"
            title="Filter by workshop type (wheel, hand-building, glaze)"
          >
            {typeOptions.map((t) => (
              <option key={t} value={t}>
                {t === "all" ? "All types (wheel, hand-building, glaze)" : t}
              </option>
            ))}
          </select>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className={`px-3 py-2 rounded-2xl border transition ${
              isDark
                ? "bg-white/10 border-white/20 text-white [&>option]:text-black [&>option]:bg-white"
                : "bg-white border-black/10 text-neutral-900"
            }`}
            aria-label="Filter by level"
            title="Filter by skill level (beginner, intermediate, advanced)"
          >
            {levelOptions.map((l) => (
              <option key={l} value={l}>
                {l === "all" ? "All levels (beginner, intermediate, advanced)" : l}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyAvailable}
              onChange={(e) => setOnlyAvailable(e.target.checked)}
              className="accent-amber-400"
            />
            Show only workshops with seats
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              className="accent-amber-400"
            />
            Show past workshops
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-amber-400"
            />
            Show archived
          </label>
          {import.meta.env.DEV && (
            <button
              onClick={() => {
                // Force clear localStorage workshop mirrors
                localStorage.removeItem('mudo:workshops/workshops.json');
                localStorage.removeItem('mudo:workshops/bookings.json');
                console.log('[WorkshopPage] Cleared localStorage mirrors, reloading...');
                window.location.reload();
              }}
              className="px-3 py-1 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs transition"
              title="DEV: Clear localStorage and reload from vault"
            >
              🔧 Clear Cache
            </button>
          )}
        </div>

        {loading && (
          <div className="text-sm opacity-70 py-10 text-center">
            Loading workshops...
          </div>
        )}
        {!loading && (
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Workshop List - takes up 2 columns on lg, full width on smaller */}
            <div className="lg:col-span-2 space-y-4">
              {filtered.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {filtered.map((w, idx) => {
                    const taken = Number(w.seats_taken || 0);
                    const cap = Number(w.capacity) || 0;
                    const left = Math.max(0, cap - taken);
                    const soldOut = left === 0;
                    const basePrice = Number(w.price_cents || 0) / 100;
                    const discount = Number(w.discountPercent || 0); // Get from workshop data
                    const price = discount > 0 ? basePrice * (1 - discount / 100) : basePrice;

                    return (
                      <motion.div
                        key={w.id}
                        id={`workshop-${w.id}`}
                        initial={innerMotion.initial}
                        animate={innerMotion.animate}
                        style={glassStyle(isDark)}
                        className="overflow-hidden"
                      >
                        {w.teaserDataUrl ? (
                          <img
                            src={w.teaserDataUrl}
                            alt={w.title}
                            className="w-full h-40 object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        ) : (
                          <div
                            className={`w-full h-40 flex items-center justify-center ${
                              isDark ? "bg-white/5" : "bg-black/5"
                            }`}
                          >
                            <span className="text-xs opacity-60">No teaser</span>
                          </div>
                        )}

                        <div className="p-4 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-lg leading-tight">
                              {w.title}
                            </h3>
                            {discount > 0 && chip(`${discount}% app-only`)}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 text-xs opacity-80">
                            <span>{fmtWhen(w.start_at || w.startISO)}</span>
                            {w.type ? chip(w.type) : null}
                            {w.skill_level ? chip(w.skill_level) : null}
                          </div>

                          {w.description && (
                            <p className="text-sm opacity-90 line-clamp-3">
                              {w.description}
                            </p>
                          )}

                          <div className="flex items-center justify-between text-sm pt-1">
                            <div className="opacity-90">
                              {fmtMoney(price)}{" "}
                              {discount > 0 ? (
                                <span className="opacity-60 line-through ml-1">
                                  {fmtMoney(basePrice)}
                                </span>
                              ) : null}
                            </div>
                            <div className="text-xs">
                              {soldOut ? (
                                <span className="px-2 py-1 rounded-full bg-red-600 text-white">
                                  Sold out
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full bg-emerald-600 text-white">
                                  {left} left
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="pt-2 flex items-center justify-between">
                            <div className="text-xs opacity-70">
                              {soldOut ? "Full" : `${left} seats left`}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="text-xs px-3 py-1.5 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors"
                                title="Duplicate this workshop"
                                onClick={() => {
                                  // Prepare data for duplication
                                  const iso = w.start_at || w.startISO;
                                  let dateStr = "";
                                  let timeStr = "";
                                  try {
                                    if (iso) {
                                      const d = new Date(iso);
                                      const y = d.getFullYear();
                                      const m = String(d.getMonth() + 1).padStart(2, "0");
                                      const dd = String(d.getDate()).padStart(2, "0");
                                      const hh = String(d.getHours()).padStart(2, "0");
                                      const mm = String(d.getMinutes()).padStart(2, "0");
                                      dateStr = `${y}-${m}-${dd}`;
                                      timeStr = `${hh}:${mm}`;
                                    }
                                  } catch {}
                                  
                                  setDuplicateWorkshop({
                                    // ❌ DO NOT include id - let upsertWorkshop generate new UUID
                                    title: w.title ? `${w.title} (Copy)` : "",
                                    description: w.description || "",
                                    date: dateStr || w.date || "",
                                    time: timeStr || w.time || "",
                                    capacity: w.capacity || 10,
                                    price: w.price_cents ? w.price_cents / 100 : w.price || 80,
                                    discountPercent: w.discountPercent || 0,
                                    type: w.type || "General",
                                    level: w.skill_level || w.level || "All Levels",
                                    materialsPerPerson: (() => {
                                      const list =
                                        (Array.isArray(w.materialsPerPerson) && w.materialsPerPerson.length
                                          ? w.materialsPerPerson
                                          : null) ||
                                        (Array.isArray(w.materials_per_person) && w.materials_per_person.length
                                          ? w.materials_per_person
                                          : []);
                                      return list.map((m) => ({ name: m?.name, qty: m?.qty, unit: m?.unit }));
                                    })(),
                                    teaserDataUrl: w.teaserDataUrl || null,
                                    isPublic: w.isPublic !== false,
                                  });
                                  setShowCreatePanel(true);
                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                              >
                                Duplicate
                              </button>
                              <button
                                type="button"
                                className="text-xs px-3 py-1.5 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                                onClick={() => {
                                  setEditIdx(idx);
                                  // Initialize isPublic and derive local date/time from start ISO
                                  const iso = w.start_at || w.startISO;
                                  let dateStr = "";
                                  let timeStr = "";
                                  try {
                                    if (iso) {
                                      const d = new Date(iso);
                                      const y = d.getFullYear();
                                      const m = String(d.getMonth() + 1).padStart(2, "0");
                                      const dd = String(d.getDate()).padStart(2, "0");
                                      const hh = String(d.getHours()).padStart(2, "0");
                                      const mm = String(d.getMinutes()).padStart(2, "0");
                                      dateStr = `${y}-${m}-${dd}`;
                                      timeStr = `${hh}:${mm}`;
                                    }
                                  } catch {}
                                  // Initialize recurring days based on the workshop's current day
                                  const initialDate = new Date(`${dateStr}T${timeStr}:00`);
                                  const dayIndex = initialDate.getDay();
                                  const dayMap = [0, 1, 2, 3, 4, 5, 6]; // Sun-Sat
                                  const initialDays = [dayMap[dayIndex]]; // Start with current day
                                  
                                  const loadedVisibility = w.is_published === true || w.isPublic === true;
                                  
                                  console.log("[WorkshopPage] Opening editor for workshop:", {
                                    id: w.id,
                                    title: w.title,
                                    "w.isPublic": w.isPublic,
                                    "w.is_published": w.is_published,
                                    "loadedVisibility": loadedVisibility
                                  });
                                  
                                  setEditForm({
                                    ...w,
                                    is_published: loadedVisibility,
                                    isPublic: loadedVisibility,
                                    date: dateStr || w.date || "",
                                    time: timeStr || w.time || "",
                                    duration_minutes: w.duration_minutes || 120,
                                    price: w.price_cents ? w.price_cents / 100 : w.price || 80,
                                    discountPercent: w.discountPercent || 0,
                                    materialsPerPerson: (() => {
                                      const list =
                                        (Array.isArray(w.materialsPerPerson) && w.materialsPerPerson.length
                                          ? w.materialsPerPerson
                                          : null) ||
                                        (Array.isArray(w.materials_per_person) && w.materials_per_person.length
                                          ? w.materials_per_person
                                          : null);
                                      return list && list.length ? list : [{ name: "", qty: "", unit: "g" }];
                                    })(),
                                    isRecurring: false,
                                    recurringType: "weekly",
                                    recurringCount: 4,
                                    recurringDays: initialDays,
                                  });
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="text-xs px-3 py-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
                                disabled={deleting === w.id || cancelling}
                                onClick={async () => {
                                  // Check if workshop has bookings directly from local state
                                  setDeleting(w.id);
                                  try {
                                    // Get bookings for this workshop from local state
                                    const workshopBookings = (bookings || []).filter(b => b.workshopId === w.id);
                                    const bookingsCount = workshopBookings.length;
                                    const hasPaidBookings = workshopBookings.some(b => b.status === 'confirmed' || b.status === 'paid');

                                    // Store booking info for this workshop
                                    setWorkshopBookings(prev => ({
                                      ...prev,
                                      [w.id]: { bookingsCount, hasPaidBookings }
                                    }));

                                    setDeleting(null);

                                    // If no bookings, allow delete
                                    if (bookingsCount === 0) {
                                      if (!confirm(`Delete "${w.title}"? This cannot be undone.`)) {
                                        return;
                                      }

                                      setDeleting(w.id);
                                      try {
                                        // Delete from context (handles both local drive + Supabase removal)
                                        if (deleteLocalWorkshop) {
                                          console.log('[WorkshopPage] Deleting workshop:', { id: w.id, title: w.title });
                                          await deleteLocalWorkshop(w.id);
                                          console.log('[WorkshopPage] Workshop deleted successfully');
                                        }

                                        notifyAndInbox?.({
                                          notification: {
                                            id: `workshop-deleted-${w.id}`,
                                            title: "Workshops",
                                            message: "Workshop deleted successfully",
                                            level: "success",
                                            source: "bookings",
                                            world: "studio",
                                            entity: { type: "workshop", id: w.id },
                                            action: { kind: "NAVIGATE", to: "/dashboard/workshops" },
                                          },
                                        });
                                      } catch (error) {
                                        console.error("Failed to delete workshop:", error);
                                        alert("Failed to delete workshop. Please try again.");
                                      } finally {
                                        setDeleting(null);
                                      }
                                    } else {
                                      // Has bookings - show cancel modal instead
                                      setCancellingWorkshop(w);
                                      setShowCancelModal(true);
                                    }
                                  } catch (error) {
                                    console.error("Failed to check workshop bookings:", error);
                                    alert("Failed to check workshop status. Please try again.");
                                    setDeleting(null);
                                  }
                                }}
                              >
                                {deleting === w.id ? "Checking..." : "Delete"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm opacity-70 text-center py-10">
                  No upcoming workshops match your filters.
                </div>
              )}
            </div>

            {/* Create Workshop Panel - sticky on lg, collapsible on mobile */}
            <AnimatePresence>
              {showCreatePanel && (
                <div className="lg:col-span-1 h-fit sticky top-24">
                  <CreateWorkshopPanel 
                    isDark={isDark}
                    initialData={duplicateWorkshop}
                    onWorkshopCreated={() => {
                      setShowCreatePanel(false);
                      setDuplicateWorkshop(null);
                      // Panel will auto-refresh the workshop list
                    }}
                  />
                </div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Edit Workshop Modal  single hero image + liquid glass panel */}
      {editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-2xl p-4">
          <motion.div
            {...modalMotion}
            className="relative w-full max-w-xl text-white overflow-hidden"
            style={glassStyle(true)}
          >
            <button
              className="absolute top-4 right-4 text-xl text-white/70 hover:text-white transition-colors w-9 h-9 flex items-center justify-center rounded-full border border-white/20 bg-black/25 hover:bg-black/40 shadow-lg"
              onClick={() => {
                setEditForm(null);
                setEditIdx(null);
              }}
              aria-label="Close"
            >
              &times;
            </button>

            <div className="p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-semibold mb-6 text-white">
                Edit Workshop
              </h2>

              <div className="flex flex-col gap-4 text-white/90">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-1">
                    Workshop Title
                  </label>
                  <input
                    className={glassInputClass}
                    placeholder="e.g., Introduction to Pottery"
                    value={editForm.title}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, title: e.target.value }))
                    }
                  />
                </div>

                {/* Workshop Visual  SINGLE hero image only */}
                <div className="flex flex-col gap-3 mt-2">
                  <label className="block text-sm font-semibold text-white">
                    Workshop Visual
                  </label>
                  <p className="text-xs text-white/65">
                    Upload a single hero image that appears across Studio and
                    Visitor worlds.
                  </p>
                  <div
                    className={`relative rounded-3xl border border-dashed border-white/20 bg-white/6 p-4 flex flex-col items-center justify-center text-center min-h-[190px] ${
                      editForm.teaserDataUrl ? "shadow-inner border-white/40" : ""
                    }`}
                  >
                    {editForm.teaserDataUrl ? (
                      <img
                        src={editForm.teaserDataUrl}
                        alt="Workshop visual"
                        className="w-full max-h-64 object-cover rounded-2xl shadow-2xl ring-1 ring-white/25"
                      />
                    ) : (
                      <>
                        <div className="text-sm font-medium text-white/85">
                          Upload a hero image
                        </div>
                        <p className="text-xs text-white/60 mt-1">
                          Drag a file here or use the button below
                        </p>
                        <p className="text-[11px] text-white/45 mt-1">
                          Recommended ratio 5:4
                        </p>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      id="edit-teaser-upload"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () =>
                          setEditForm((f) => ({
                            ...f,
                            teaserDataUrl: reader.result,
                          }));
                        reader.readAsDataURL(file);
                      }}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 rounded-2xl border border-white/25 bg-white/15 hover:bg-white/25 text-white text-sm font-semibold transition-colors shadow-sm"
                      onClick={() =>
                        document.getElementById("edit-teaser-upload").click()
                      }
                    >
                      Upload Image
                    </button>
                    {editForm.teaserDataUrl && (
                      <button
                        type="button"
                        className="px-3 py-2 text-sm text-red-200 border border-white/20 rounded-2xl hover:bg-white/5 transition-colors"
                        onClick={() =>
                          setEditForm((f) => ({ ...f, teaserDataUrl: null }))
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-1">
                    Description
                  </label>
                  <textarea
                    className={`${glassInputClass} resize-none min-h-[90px]`}
                    placeholder="Describe what participants will learn and create..."
                    rows={3}
                    value={editForm.description || ""}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-1">
                    Location
                  </label>
                  <input
                    className={glassInputClass}
                    placeholder="e.g., 123 Main St, Sydney or Studio 2, Bark Line Studio"
                    value={editForm.location || ""}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, location: e.target.value }))
                    }
                  />
                  <p className="text-xs text-white/65 mt-1">
                    This will show on the visitor page
                  </p>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      className={glassInputClass}
                      value={editForm.date || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, date: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white mb-1">
                      Time
                    </label>
                    <input
                      type="time"
                      className={glassInputClass}
                      value={editForm.time || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, time: e.target.value }))
                      }
                    />
                  </div>
                </div>

                {/* Duration selector with end time preview */}
                <div className="space-y-3">
                  <label className="block">
                    <div className="text-sm font-semibold text-white mb-2">
                      Duration (30-min increments)
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[30, 60, 90, 120, 150, 180, 210, 240].map((mins) => (
                        <button
                          key={mins}
                          type="button"
                          onClick={() => setEditForm((f) => ({ ...f, duration_minutes: mins }))}
                          className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                            (editForm.duration_minutes || 120) === mins
                              ? "bg-[var(--mudo-primary)] text-slate-900 shadow-lg shadow-amber-400/20"
                              : "bg-slate-700/50 border border-white/20 text-slate-100 hover:bg-slate-600/50"
                          }`}
                        >
                          {mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ""}`}
                        </button>
                      ))}
                    </div>
                  </label>

                  {/* End time preview */}
                  {editForm.date && editForm.time && (
                    <div className="text-xs bg-slate-800/40 border border-white/10 rounded-lg p-3 space-y-1">
                      {(() => {
                        const startDt = new Date(editForm.date + "T" + editForm.time);
                        const endDt = new Date(startDt.getTime() + (editForm.duration_minutes || 120) * 60000);
                        const formatter = new Intl.DateTimeFormat("en-AU", {
                          timeZone: "Australia/Sydney",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        });
                        return (
                          <>
                            <p className="text-slate-300">
                              <strong>Starts:</strong> {formatter.format(startDt)}
                            </p>
                            <p className="text-slate-300">
                              <strong>Ends:</strong> {formatter.format(endDt)}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Recurring Workshop */}
                <div className="p-4 rounded-2xl border border-white/15 bg-white/8">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editForm.isRecurring || false}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          isRecurring: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 accent-amber-400"
                    />
                    <span className="text-sm font-semibold text-white">
                      Recurring Workshop
                    </span>
                  </label>

                  <AnimatePresence>
                    {editForm.isRecurring && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 space-y-3 overflow-hidden"
                      >
                        <div>
                          <label className="block text-xs font-semibold text-white/80 mb-2">
                            Frequency
                          </label>
                          <select
                            value={editForm.recurringType || "weekly"}
                            onChange={(e) =>
                              setEditForm((f) => ({
                                ...f,
                                recurringType: e.target.value,
                              }))
                            }
                            className={`${glassInputClass} text-neutral-900`}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>

                        {editForm.recurringType === "weekly" && (
                          <div>
                            <label className="block text-xs font-semibold text-white/80 mb-2">
                              Days of Week
                            </label>
                            <div className="grid grid-cols-4 gap-2">
                              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, idx) => (
                                <label key={day} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={(editForm.recurringDays || []).includes(idx)}
                                    onChange={(e) => {
                                      const days = editForm.recurringDays || [];
                                      if (e.target.checked) {
                                        setEditForm((f) => ({
                                          ...f,
                                          recurringDays: [...new Set([...days, idx])].sort(),
                                        }));
                                      } else {
                                        setEditForm((f) => ({
                                          ...f,
                                          recurringDays: days.filter((d) => d !== idx),
                                        }));
                                      }
                                    }}
                                    className="w-3 h-3 accent-amber-400"
                                  />
                                  <span className="text-xs text-white/90 font-medium">{day}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-xs font-semibold text-white/80 mb-2">
                            Number of Sessions
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="52"
                            value={editForm.recurringCount || 4}
                            onChange={(e) =>
                              setEditForm((f) => ({
                                ...f,
                                recurringCount: Number(e.target.value),
                              }))
                            }
                            className={glassInputClass}
                          />
                        </div>

                        <div className="text-xs text-white/70 p-2 rounded bg-white/5 border border-white/10">
                          This will create{" "}
                          <span className="font-semibold text-white">
                            {editForm.recurringCount || 4} workshops
                          </span>{" "}
                          {editForm.recurringType === "daily" && "daily"}
                          {editForm.recurringType === "weekly" && `every week on ${(editForm.recurringDays || []).map(d => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]).join(", ")}`}
                          {editForm.recurringType === "biweekly" && "every 2 weeks"}
                          {editForm.recurringType === "monthly" && "every month"}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Capacity, Price, and Discount */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-1">
                      Attendees
                    </label>
                    <input
                      type="number"
                      className={glassInputClass}
                      placeholder="Max capacity"
                      min="1"
                      value={editForm.capacity}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          capacity: e.target.value,
                        }))
                      }
                    />
                    <p className="text-xs text-white/65 mt-1">
                      Maximum number of participants
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white mb-1">
                      Cost (AUD)
                    </label>
                    <input
                      type="number"
                      className={glassInputClass}
                      placeholder="80"
                      min="0"
                      step="0.01"
                      value={editForm.price}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, price: e.target.value }))
                      }
                    />
                    <p className="text-xs text-white/65 mt-1">
                      Price per person
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white mb-1">
                      App Discount %
                    </label>
                    <input
                      type="number"
                      className={glassInputClass}
                      placeholder="0"
                      min="0"
                      max="100"
                      value={editForm.discountPercent || 0}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, discountPercent: Number(e.target.value) }))
                      }
                    />
                    <p className="text-xs text-white/65 mt-1">
                      {editForm.discountPercent > 0 && (
                        <span className="text-emerald-300">
                          ${((editForm.price || 0) * (1 - (editForm.discountPercent || 0) / 100)).toFixed(2)} final
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Materials Per Participant */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">
                    Materials Per Participant
                  </label>
                  <div className="space-y-2">
                    {(editForm.materialsPerPerson || []).map((material, idx) => (
                      <div key={idx} className="grid grid-cols-[2fr,1fr,1fr,auto] gap-2 items-end">
                        <div>
                          <input
                            list={materialsDatalistId}
                            type="text"
                            placeholder="Material name"
                            value={material.name || ""}
                            onChange={(e) => {
                              const updated = [...(editForm.materialsPerPerson || [])];
                              updated[idx] = { ...updated[idx], name: e.target.value };
                              setEditForm((f) => ({ ...f, materialsPerPerson: updated }));
                            }}
                            className={`${glassInputClass} text-sm`}
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            placeholder="Qty"
                            min="0"
                            step="0.1"
                            value={material.qty || ""}
                            onChange={(e) => {
                              const updated = [...(editForm.materialsPerPerson || [])];
                              updated[idx] = { ...updated[idx], qty: e.target.value };
                              setEditForm((f) => ({ ...f, materialsPerPerson: updated }));
                            }}
                            className={`${glassInputClass} text-sm`}
                          />
                        </div>
                        <div>
                          <select
                            value={material.unit || "g"}
                            onChange={(e) => {
                              const updated = [...(editForm.materialsPerPerson || [])];
                              updated[idx] = { ...updated[idx], unit: e.target.value };
                              setEditForm((f) => ({ ...f, materialsPerPerson: updated }));
                            }}
                            className={`${glassInputClass} text-neutral-900 text-sm`}
                          >
                            <option value="g">g</option>
                            <option value="kg">kg</option>
                            <option value="ml">ml</option>
                            <option value="L">L</option>
                            <option value="pcs">pcs</option>
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = (editForm.materialsPerPerson || []).filter((_, i) => i !== idx);
                            setEditForm((f) => ({ ...f, materialsPerPerson: updated }));
                          }}
                          className="px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...(editForm.materialsPerPerson || []), { name: "", qty: "", unit: "g" }];
                        setEditForm((f) => ({ ...f, materialsPerPerson: updated }));
                      }}
                      className="text-xs px-3 py-2 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 text-white transition-colors"
                    >
                      + Add Material
                    </button>
                  </div>

                  <datalist id={materialsDatalistId}>
                    {inventoryMaterialNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>

                  <p className="text-xs text-white/65 mt-2">
                    These quantities are per person. Total will be multiplied by capacity.
                  </p>
                </div>

                {/* Discount */}
                <div>
                  <label className="block text-sm font-semibold text-white mb-1">
                    App Discount %
                  </label>
                  <input
                    type="number"
                    className={glassInputClass}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="1"
                    value={editForm.discountPercent || 0}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, discountPercent: Number(e.target.value) }))
                    }
                  />
                  <p className="text-xs text-white/65 mt-1">
                    Discount for app users (0-100%). {editForm.discountPercent > 0 && `App price: A$${(editForm.price * (1 - (editForm.discountPercent || 0) / 100)).toFixed(2)}`}
                  </p>
                </div>

                {/* Visibility Toggle */}
                <div className="flex items-center justify-between p-4 rounded-3xl border border-white/18 bg-white/8">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-white">
                      Visitor Visibility
                    </label>
                    <p className="text-xs text-white/65 mt-1">
                      Control whether this workshop appears in the visitor world
                    </p>
                  </div>
                  <button
                    type="button"
                    className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                      !!editForm?.is_published ? "bg-emerald-400/85" : "bg-white/20"
                    }`}
                    onClick={() => handleVisibilityToggle(!editForm?.is_published)}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                        !!editForm?.is_published ? "translate-x-8" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                <p className="text-xs text-center">
                  {editForm?.is_published ? (
                    <span className="text-emerald-300 font-medium">
                      Visible to visitors
                    </span>
                  ) : (
                    <span className="text-white/70 font-medium">
                      Hidden from visitors
                    </span>
                  )}
                </p>

                {/* Save and Add to Planner Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    className="mt-4 px-6 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white font-semibold transition-colors shadow-[0_14px_35px_rgba(15,118,220,0.55)] flex items-center justify-center gap-2"
                    disabled={saving}
                    onClick={async () => {
                      // Guard: prevent publishing paid workshops unless Stripe payouts are connected.
                      try {
                        const priceNum = Number(editForm?.price || 0);
                        const isPaid = priceNum > 0;
                        const willBePublic = !!editForm?.isPublic;
                        if (isPaid && willBePublic && !isStripeConnected) {
                          await ensureStripeConnected({ studioId });
                          return; // abort save until Stripe is connected
                        }
                      } catch {}
                      setSaving(true);
                      
                      // ✅ CRITICAL FIX: Define localId at function scope (not inside if/else)
                      let localId = editForm?.id;
                      
                      try {
                        if (editForm.isRecurring) {
                          // First, save the base workshop with recurring metadata
                          const baseWorkshop = {
                            id: editForm.id,
                            title: editForm.title || "",
                            description: editForm.description || "",
                            type: editForm.type || "General",
                            level: editForm.level || "All Levels",
                            date: editForm.date || "",
                            time: editForm.time || "",
                            startISO: editForm.startISO,
                            capacity: Number(editForm.capacity) || 10,
                            price: Number(editForm.price) || 0,
                            discountPercent: Number(editForm.discountPercent) || 0,
                            teaserDataUrl: editForm.teaserDataUrl || null,
                            isPublic: editForm.isPublic !== false,
                            materialsPerPerson: Array.isArray(editForm.materialsPerPerson) ? editForm.materialsPerPerson : [],
                            networkId: editForm.networkId,
                            createdAt: editForm.createdAt,
                            updatedAt: new Date().toISOString(),
                            isRecurring: true,
                            recurringType: editForm.recurringType || "weekly",
                            recurringCount: editForm.recurringCount || 4,
                            recurringDays: editForm.recurringDays || [],
                          };
                          await upsertWorkshop(baseWorkshop);

                          // Create multiple workshops for recurring
                          const baseDate = new Date(`${editForm.date}T${editForm.time}:00`);
                          const selectedDays = editForm.recurringDays || [baseDate.getDay()];
                          let createdCount = 0;
                          let currentDate = new Date(baseDate);

                          while (createdCount < (editForm.recurringCount || 4)) {
                            const currentDay = currentDate.getDay();

                            // For weekly recurring, only create if current day matches selected days
                            const shouldCreate = editForm.recurringType === "weekly" 
                              ? selectedDays.includes(currentDay)
                              : true;

                            if (shouldCreate) {
                              const workshopData = {
                                id: createdCount === 0 ? editForm.id : `${editForm.id}-recurring-${createdCount}`,
                                title: `${editForm.title} (${createdCount + 1}/${editForm.recurringCount || 4})`,
                                description: editForm.description || "",
                                type: editForm.type || "General",
                                level: editForm.level || "All Levels",
                                date: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`,
                                time: `${String(currentDate.getHours()).padStart(2, "0")}:${String(currentDate.getMinutes()).padStart(2, "0")}`,
                                startISO: editForm.startISO,
                                capacity: Number(editForm.capacity) || 10,
                                price: Number(editForm.price) || 0,
                                discountPercent: Number(editForm.discountPercent) || 0,
                                teaserDataUrl: editForm.teaserDataUrl || null,
                                isPublic: editForm.isPublic !== false,
                                materialsPerPerson: Array.isArray(editForm.materialsPerPerson) ? editForm.materialsPerPerson : [],
                                networkId: editForm.networkId,
                                createdAt: editForm.createdAt,
                                isRecurring: false,
                                recurringDays: undefined,
                              };

                              // ✅ CRITICAL: Capture returned workshop from Supabase
                              const savedWorkshop = await upsertWorkshop(workshopData);
                              const localId = savedWorkshop.id || savedWorkshop;

                              // ✅ Update local workshops list with returned row (keeps same id forever)
                              setWorkshops(prev => {
                                const ix = prev.findIndex(w => w.id === localId);
                                if (ix >= 0) {
                                  const next = [...prev];
                                  next[ix] = typeof savedWorkshop === 'object' ? savedWorkshop : next[ix];
                                  return next;
                                }
                                return typeof savedWorkshop === 'object' ? [...prev, savedWorkshop] : prev;
                              });

                              let effectiveStartISO = workshopData.startISO;
                              if (!effectiveStartISO) {
                                if (workshopData.date && workshopData.time) {
                                  effectiveStartISO = new Date(`${workshopData.date}T${workshopData.time}:00`).toISOString();
                                } else {
                                  effectiveStartISO = new Date(Date.now() + 60 * 60 * 1000).toISOString();
                                }
                              }

                              // Supabase auto-sync disabled: recurring saves are local-only.
                              console.warn('[WorkshopPage] Supabase auto-sync disabled (recurring). Save is local-only.');

                              // Track createdCount only; do not shadow global workshops list
                              createdCount++;
                            }

                            // Advance date based on frequency
                            if (editForm.recurringType === "daily") {
                              currentDate.setDate(currentDate.getDate() + 1);
                            } else if (editForm.recurringType === "weekly") {
                              currentDate.setDate(currentDate.getDate() + 1);
                            } else if (editForm.recurringType === "biweekly") {
                              currentDate.setDate(currentDate.getDate() + 1);
                            } else if (editForm.recurringType === "monthly") {
                              currentDate.setMonth(currentDate.getMonth() + 1);
                            }
                          }
                        } else {
                          // Single workshop update - ensure all fields are included
                          const cleanedWorkshop = {
                            id: editForm.id,
                            title: editForm.title || "",
                            description: editForm.description || "",
                            type: editForm.type || "General",
                            level: editForm.level || "All Levels",
                            date: editForm.date || "",
                            time: editForm.time || "",
                            startISO: editForm.startISO,
                            duration_minutes: Number(editForm.duration_minutes) || 120,  // ✅ ADD: Duration for end_at computation
                            capacity: Number(editForm.capacity) || 10,
                            price_cents: (Number(editForm.price) || 0) * 100,
                            discountPercent: Number(editForm.discountPercent) || 0,
                            teaserDataUrl: editForm.teaserDataUrl || null,
                            isPublic: editForm.isPublic !== false,
                            materialsPerPerson: Array.isArray(editForm.materialsPerPerson) ? editForm.materialsPerPerson : [],
                            networkId: editForm.networkId,
                            createdAt: editForm.createdAt,
                            updatedAt: new Date().toISOString(),
                            isRecurring: editForm.isRecurring || false,
                            recurringType: editForm.recurringType || "weekly",
                            recurringCount: editForm.recurringCount || 4,
                            recurringDays: editForm.recurringDays || [],
                          };

                          // ✅ CRITICAL: Capture returned workshop from Supabase
                          const savedWorkshop = await upsertWorkshop(cleanedWorkshop);
                          localId = savedWorkshop.id || savedWorkshop;  // ✅ Update localId in outer scope

                          // ✅ Update editForm state with returned row (keeps same id forever)
                          if (typeof savedWorkshop === 'object') {
                            setEditForm(prev => ({ ...prev, ...savedWorkshop }));
                          }

                          // ✅ Update local workshops list by id
                          setWorkshops(prev => {
                            const ix = prev.findIndex(w => w.id === localId);
                            if (ix >= 0) {
                              const next = [...prev];
                              next[ix] = typeof savedWorkshop === 'object' ? savedWorkshop : next[ix];
                              return next;
                            }
                            return typeof savedWorkshop === 'object' ? [...prev, savedWorkshop] : prev;
                          });

                          // Supabase auto-sync disabled: single saves are local-only.
                          console.warn('[WorkshopPage] Supabase auto-sync disabled. Save is local-only.');
                        }
                        // Visibility is handled exclusively by the toggle; no publish/unpublish here

                        setEditForm(null);
                        setEditIdx(null);
                      } catch (error) {
                        console.error("Failed to save workshop:", error);
                        // Only show alert for critical errors, not for RLS sync issues
                        if (error?.code !== '42501' && error?.status !== 403) {
                          alert("Failed to save workshop. Please try again.");
                        }
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                  <button
                    className="mt-4 px-6 py-3 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-colors shadow-[0_14px_35px_rgba(168,85,247,0.55)]"
                    onClick={() => {
                      // Add workshop to planner
                      try {
                        const workshopDate = editForm.date && editForm.time 
                          ? new Date(`${editForm.date}T${editForm.time}:00`)
                          : new Date();
                        
                        const plannerEvent = {
                          title: editForm.title || "Workshop",
                          start: workshopDate.toISOString(),
                          end: new Date(workshopDate.getTime() + 2 * 60 * 60 * 1000).toISOString(),
                          category: "workshop",
                          notes: editForm.description || "",
                          status: "planned",
                          tags: ["workshop", editForm.type, editForm.level].filter(Boolean),
                        };
                        
                        upsertEvents([plannerEvent]);
                        
                        notifyAndInbox?.({
                          notification: {
                            id: `workshop-planner-${Date.now()}`,
                            title: "Added to Planner",
                            message: `"${editForm.title}" has been added to your planner`,
                            level: "success",
                            source: "workshops",
                            world: "studio",
                          },
                        });
                      } catch (err) {
                        console.error("Failed to add to planner:", err);
                        alert("Failed to add workshop to planner");
                      }
                    }}
                  >
                    → Add to Planner
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Booking modal removed in Supabase-only listing view (bookings handled elsewhere) */}

      {/* Cancel Workshop Modal */}
      <AnimatePresence>
        {showCancelModal && cancellingWorkshop && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.7)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!cancelling) {
                setShowCancelModal(false);
                setCancellingWorkshop(null);
                setCancelReason('');
              }
            }}
          >
            <motion.div
              className="w-full max-w-lg rounded-2xl p-6 shadow-2xl"
              style={glassStyle(isDark)}
              variants={modalMotion}
              initial="initial"
              animate="animate"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold mb-4">
                Cancel Workshop & Refund Attendees
              </h2>
              <p className="text-sm opacity-80 mb-4">
                This workshop has <strong>{workshopBookings[cancellingWorkshop.id]?.bookingsCount || 0} booking(s)</strong>.
                {workshopBookings[cancellingWorkshop.id]?.hasPaidBookings && (
                  <> All paid attendees will be <strong>automatically refunded</strong> via Stripe.</>
                )}
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Workshop: <strong>{cancellingWorkshop.title}</strong>
                </label>
                <label className="block text-sm font-medium mb-2">
                  Cancel Reason (optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
                  rows={3}
                  placeholder="e.g., Instructor unavailable, venue closed, etc."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={cancelling}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellingWorkshop(null);
                    setCancelReason('');
                  }}
                  disabled={cancelling}
                >
                  Keep Workshop
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
                  disabled={cancelling}
                  onClick={async () => {
                    setCancelling(true);
                    try {
                      const token = (await supabase.auth.getSession())?.data?.session?.access_token;
                      if (!token) {
                        alert('Please sign in to cancel workshops');
                        return;
                      }

                      console.log('[WorkshopPage] Cancelling workshop:', cancellingWorkshop.id);

                      // Call cancel API
                      const cancelResponse = await fetch('/api/workshops/cancel', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          workshopId: cancellingWorkshop.id,
                          cancelReason: cancelReason || undefined
                        })
                      });

                      if (!cancelResponse.ok) {
                        const errorData = await cancelResponse.json();
                        throw new Error(errorData.error || 'Failed to cancel workshop');
                      }

                      const result = await cancelResponse.json();
                      console.log('[WorkshopPage] Workshop cancelled:', result);

                      // Remove from local state (workshop is now cancelled)
                      if (deleteLocalWorkshop) {
                        await deleteLocalWorkshop(cancellingWorkshop.id);
                      }

                      // Broadcast cache invalidation event
                      window.dispatchEvent(
                        new CustomEvent('workshops:changed', {
                          detail: {
                            studioId: studioId,
                            workshopId: cancellingWorkshop.id,
                            action: 'cancelled'
                          }
                        })
                      );

                      notifyAndInbox?.({
                        notification: {
                          id: `workshop-cancelled-${cancellingWorkshop.id}`,
                          title: "Workshop Cancelled",
                          message: `Workshop cancelled with ${result.refundedCount} refund(s) processed`,
                          level: "success",
                          source: "bookings",
                          world: "studio",
                          entity: { type: "workshop", id: cancellingWorkshop.id },
                          action: { kind: "NAVIGATE", to: "/dashboard/workshops" },
                        },
                      });

                      // Close modal
                      setShowCancelModal(false);
                      setCancellingWorkshop(null);
                      setCancelReason('');
                    } catch (error) {
                      console.error('[WorkshopPage] Failed to cancel workshop:', error);
                      alert(`Failed to cancel workshop: ${error.message}`);
                    } finally {
                      setCancelling(false);
                    }
                  }}
                >
                  {cancelling ? 'Cancelling & Refunding...' : 'Cancel & Refund Now'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}





